import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  getGmailOAuthStatus,
  getGmailOAuthAuthUrl,
  disconnectGmailOAuth,
} from '../shared/api/gmailOAuth';
import {
  saveAccountEmailSmtp,
  clearAccountEmailSmtp,
  saveSystemEmailSmtp,
  clearSystemEmailSmtp,
} from '../shared/api/accountEmailSmtp';
import { getApiRoleFromUserInfo } from '../shared/utils/apiRoleFromUserInfo';


import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

const TAB_MESSAGES = 'messages';
const TAB_EMAIL = 'email';
const VALID_TABS = [TAB_MESSAGES, TAB_EMAIL];
const DEFAULT_TAB = TAB_MESSAGES;

const ROW_HEIGHT = 20;
const MIN_ROWS = 4;
const MAX_HEIGHT_EMAIL = 420;
const MAX_HEIGHT_SMS = 200;

function AutoResizeTextarea({ value, onChange, placeholder, style = {}, minRows = MIN_ROWS, maxHeight = MAX_HEIGHT_EMAIL, ...rest }) {
  const ref = useRef(null);

  const adjustHeight = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    const capped = Math.min(Math.max(el.scrollHeight, minRows * ROW_HEIGHT), maxHeight);
    el.style.height = `${capped}px`;
    el.style.overflowY = capped >= maxHeight ? 'auto' : 'hidden';
  }, [minRows, maxHeight]);

  useEffect(() => {
    adjustHeight();
  }, [value, adjustHeight]);

  function handleChange(e) {
    onChange(e);
    requestAnimationFrame(adjustHeight);
  }

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={handleChange}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '8px',
        fontFamily: 'monospace',
        fontSize: '13px',
        resize: 'none',
        minHeight: minRows * ROW_HEIGHT,
        boxSizing: 'border-box',
        ...style,
      }}
      {...rest}
    />
  );
}

const MESSAGE_LABELS = {
  event_registration_confirmation: 'Event Registration Confirmation',
  event_waitlist: 'Event Waitlist',
  event_waitlist_promoted_confirmation: 'Event Waitlist → Confirmed (spot opened)',
  event_registration_cancellation: 'Event Registration Cancellation',
  event_reminder_2days: 'Event Reminder (within next 2 days)',
  survey_invitation: 'Survey Invitation',
  user_signup_welcome: 'User Signup Welcome',
  user_invite: 'User Invite',
  user_password_reset: 'Password Reset',
  user_verification_code: 'User Verification Code',
};

const PLACEHOLDER_HINT = 'Placeholders: {{displayName}}, {{role}}, {{email}}, {{passwordResetLink}}, {{verificationCode}}, {{verificationLink}}, {{eventName}}, {{dateTimeStr}}, {{locationStr}}, {{surveyName}}, {{surveyUrl}}, {{cancellationLink}}, {{eventCalendarLink}}, {{brandName}} (Account organization full name when the server sends for that account). For event emails with check-in QR: {{qrHtml}} (registration confirmation, waitlist, reminder). Optional: {{#if role}}...{{/if}}, {{#if passwordResetLink}}...{{/if}}, {{#if verificationLink}}...{{/if}}, {{#if locationStr}}...{{/if}}, {{#if surveyUrl}}...{{/if}}, {{#if cancellationLink}}...{{/if}}, {{#if eventCalendarLink}}...{{/if}}';

function MessagesTab({ account_id, serverUrl }) {
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const messageKeysAll = Object.keys(MESSAGE_LABELS);
  const [filterTypes, setFilterTypes] = useState(() => Object.keys(MESSAGE_LABELS));
  const [restoreDialog, setRestoreDialog] = useState({ open: false, messageKeys: [] });

  const fetchSettings = useCallback(() => {
    setLoading(true);
    setBannerError('');
    apiFetch(serverUrl + '/getCommunication', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then((res) => res.json())
      .then((data) => {
        setTemplates(data.templates || {});
      })
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl]);

  const saveTemplates = useCallback(async (nextTemplates) => {
    if (!account_id) return null;
    setSaving(true);
    setBannerError('');
    setSaveSuccess(false);
    try {
      const res = await apiFetch(serverUrl + '/saveCommunication', {
        method: 'POST',
        body: JSON.stringify({ account_id, templates: nextTemplates }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || 'Unable to save communication settings.');
      }
      setTemplates(data.templates || {});
      setSaveSuccess(true);
      return data.templates || {};
    } finally {
      setSaving(false);
    }
  }, [account_id, serverUrl]);

  useEffect(() => {
    if (account_id) fetchSettings();
    else setLoading(false);
  }, [account_id, fetchSettings]);

  function handleChange(messageKey, field, value) {
    setTemplates((prev) => ({
      ...prev,
      [messageKey]: {
        ...(prev[messageKey] || {}),
        [field]: value,
      },
    }));
    setSaveSuccess(false);
  }

  function handleSave() {
    saveTemplates(templates).catch((err) => setBannerError(getMessageForFetchError(err)));
  }

  async function restoreDefaultsForKeys(messageKeys) {
    const keys = Array.isArray(messageKeys) ? messageKeys.filter(Boolean) : [];
    if (keys.length === 0) return;
    const nextTemplates = {
      ...templates,
    };
    for (const key of keys) {
      nextTemplates[key] = {
        ...(templates[key] || {}),
        emailSubject: '',
        emailBodyHtml: '',
        smsBody: '',
      };
    }
    await saveTemplates(nextTemplates);
    fetchSettings();
  }

  if (loading) {
    return <p>Loading…</p>;
  }

  return (
    <>
      <p style={{ color: 'var(--app-text-muted)', marginBottom: '16px' }}>
        Customize email and SMS message content for events, surveys, and user account emails. Turn email or SMS on or off per message type.
        The event reminder is sent for events that start tomorrow or the day after (account time zone). Events that start today are not included.
        User Signup Welcome is sent after Event Calendar public signup. User Invite is sent from User Admin invite/resend. Password Reset is sent from forgot-password.
      </p>
      <p style={{ fontSize: '12px', color: 'var(--app-text-muted)', marginBottom: '24px' }}>
        {PLACEHOLDER_HINT}
      </p>

      {bannerError && <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />}
      {saveSuccess && (
        <div style={{ padding: '12px', marginBottom: '16px', background: '#d1e7dd', color: '#0f5132', borderRadius: '6px' }}>
          Message settings saved.
        </div>
      )}

      <ConfirmationModal
        open={restoreDialog.open}
        title="Restore default message"
        message={
          restoreDialog.messageKeys && restoreDialog.messageKeys.length > 0
            ? `Restore the default subject/body/SMS for ${restoreDialog.messageKeys.length} selected message type(s)? This will remove your custom text for those message types.`
            : 'Restore the default message(s)?'
        }
        mode="confirm"
        confirmLabel="Restore"
        onCancel={() => setRestoreDialog({ open: false, messageKeys: [] })}
        onConfirm={async () => {
          const keys = restoreDialog.messageKeys;
          await restoreDefaultsForKeys(keys);
          setRestoreDialog({ open: false, messageKeys: [] });
        }}
      />

      <div style={{ marginBottom: '20px', padding: '12px 16px', background: 'var(--app-surface)', border: '1px solid var(--app-border)', borderRadius: '8px' }}>
        <div style={{ fontWeight: 600, marginBottom: '10px', fontSize: '14px' }}>Show message types</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 20px', alignItems: 'center' }}>
          {messageKeysAll.map((key) => (
            <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
              <input
                type="checkbox"
                checked={filterTypes.includes(key)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilterTypes((prev) => (prev.includes(key) ? prev : [...prev, key]));
                  } else {
                    setFilterTypes((prev) => prev.filter((k) => k !== key));
                  }
                }}
              />
              {MESSAGE_LABELS[key]}
            </label>
          ))}
          <span style={{ color: 'var(--app-text-muted)', fontSize: '13px' }}>
            <button
              type="button"
              onClick={() => setFilterTypes(messageKeysAll.slice())}
              style={{ background: 'none', border: 'none', color: 'var(--app-primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              All
            </button>
            {' · '}
            <button
              type="button"
              onClick={() => setFilterTypes([])}
              style={{ background: 'none', border: 'none', color: 'var(--app-primary)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              None
            </button>
          </span>
        </div>
      </div>

      {filterTypes.length === 0 && (
        <p style={{ color: 'var(--app-text-muted)', marginBottom: '16px' }}>Select at least one message type above to view and edit.</p>
      )}

      {filterTypes.map((key) => {
        const t = templates[key] || {};
        const label = MESSAGE_LABELS[key];
        return (
          <section
            key={key}
            style={{
              border: '1px solid var(--app-border)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              background: 'var(--app-canvas)',
            }}
          >
            <h2 style={{ fontSize: '18px', marginTop: 0, marginBottom: '16px' }}>{label}</h2>

            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={t.emailEnabled !== false}
                  onChange={(e) => handleChange(key, 'emailEnabled', e.target.checked)}
                />
                Send email
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={t.smsEnabled !== false}
                  onChange={(e) => handleChange(key, 'smsEnabled', e.target.checked)}
                />
                Send SMS
              </label>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Email subject</label>
              <input
                type="text"
                value={t.emailSubject || ''}
                onChange={(e) => handleChange(key, 'emailSubject', e.target.value)}
                placeholder="e.g. Registration confirmed: {{eventName}}"
                style={{ width: '100%', maxWidth: '500px', padding: '8px' }}
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Email body</label>
              <AutoResizeTextarea
                value={t.emailBodyHtml || ''}
                onChange={(e) => handleChange(key, 'emailBodyHtml', e.target.value)}
                placeholder="HTML content. Leave empty to use default. A plain-text version is derived automatically for fallback."
                minRows={6}
                maxHeight={MAX_HEIGHT_EMAIL}
              />
            </div>

            <div style={{ marginBottom: '0' }}>
              <label className="form-label" style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>SMS body</label>
              <AutoResizeTextarea
                value={t.smsBody || ''}
                onChange={(e) => handleChange(key, 'smsBody', e.target.value)}
                placeholder="SMS content. Leave empty to use default."
                minRows={3}
                maxHeight={MAX_HEIGHT_SMS}
              />
            </div>
          </section>
        );
      })}

      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? 'Saving…' : 'Save messages'}
      </button>

      <button
        type="button"
        className="btn btn-secondary"
        onClick={() => setRestoreDialog({ open: true, messageKeys: filterTypes.slice() })}
        disabled={saving || filterTypes.length === 0}
        style={{ marginLeft: '12px' }}
      >
        Restore default
      </button>
    </>
  );
}

function fromSampleLine(accountName, email) {
  const brand = (accountName || '').trim();
  const addr = (email || '').trim() || 'you@gmail.com';
  return brand ? `"${brand}" <${addr}>` : addr;
}

function emptyEmailForm() {
  return {
    host: 'smtp.gmail.com',
    port: '587',
    secure: false,
    user: '',
    pass: '',
  };
}

function formFromPublicSmtp(smtp) {
  return {
    host: smtp.host || 'smtp.gmail.com',
    port: smtp.port != null && smtp.port !== '' ? String(smtp.port) : '587',
    secure: !!smtp.secure,
    user: smtp.user || '',
    pass: '',
  };
}

function AuthModeToggle({ idPrefix, value, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }} role="radiogroup" aria-label="Email auth method">
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: disabled ? 'default' : 'pointer' }}>
        <input
          type="radio"
          name={`${idPrefix}-auth-mode`}
          checked={value === 'oauth'}
          onChange={() => onChange('oauth')}
          disabled={disabled}
        />
        Google OAuth
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: disabled ? 'default' : 'pointer' }}>
        <input
          type="radio"
          name={`${idPrefix}-auth-mode`}
          checked={value === 'smtp'}
          onChange={() => onChange('smtp')}
          disabled={disabled}
        />
        SMTP (app password)
      </label>
    </div>
  );
}

function SmtpFields({ idPrefix, form, onField, configured, smtpUserPlaceholder, accountNameSample }) {
  const userPlaceholder = (smtpUserPlaceholder || '').trim() || 'you@gmail.com';
  const userForSample = (form.user || '').trim() || userPlaceholder;
  const brand = (accountNameSample || '').trim();
  const fromSample = brand
    ? `"${brand}" <${userForSample}>`
    : userForSample;
  return (
    <>
      <div style={{ marginBottom: '12px' }}>
        <label className="form-label" htmlFor={`${idPrefix}-host`}>SMTP host</label>
        <input
          id={`${idPrefix}-host`}
          type="text"
          value={form.host}
          onChange={(e) => onField('host', e.target.value)}
          placeholder="smtp.gmail.com"
          style={{ width: '100%', padding: '8px' }}
        />
      </div>

      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={{ flex: '1 1 120px' }}>
          <label className="form-label" htmlFor={`${idPrefix}-port`}>Port</label>
          <input
            id={`${idPrefix}-port`}
            type="text"
            inputMode="numeric"
            value={form.port}
            onChange={(e) => onField('port', e.target.value)}
            placeholder="587"
            style={{ width: '100%', padding: '8px' }}
          />
        </div>
        <div style={{ flex: '1 1 160px', display: 'flex', alignItems: 'flex-end', paddingBottom: '8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={form.secure}
              onChange={(e) => onField('secure', e.target.checked)}
            />
            Secure (TLS/SSL)
          </label>
        </div>
      </div>

      <div style={{ marginBottom: '12px' }}>
        <label className="form-label" htmlFor={`${idPrefix}-user`}>SMTP user (email)</label>
        <input
          id={`${idPrefix}-user`}
          type="email"
          autoComplete="off"
          value={form.user}
          onChange={(e) => onField('user', e.target.value)}
          placeholder={userPlaceholder}
          style={{ width: '100%', padding: '8px' }}
        />
        <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--app-text-muted)' }}>
          Example From in the inbox: {fromSample}. Display name comes from Account Admin (organization full name).
        </p>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <label className="form-label" htmlFor={`${idPrefix}-pass`}>
          SMTP password {configured ? '(leave blank to keep current)' : '(app password)'}
        </label>
        <input
          id={`${idPrefix}-pass`}
          type="password"
          autoComplete="new-password"
          value={form.pass}
          onChange={(e) => onField('pass', e.target.value)}
          placeholder={configured ? '••••••••' : 'Gmail app password'}
          style={{ width: '100%', padding: '8px' }}
        />
      </div>
    </>
  );
}

function EmailOauthPanel({
  statusLine,
  connected,
  connectedEmail,
  accountNameSample,
  onConnect,
  onDisconnect,
  connectLabel,
  disconnectLabel,
  busy,
  disabled,
}) {
  return (
    <>
      <p style={{ margin: '0 0 12px', fontSize: '14px' }}>{statusLine}</p>
      {connected && connectedEmail ? (
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--app-text-muted)' }}>
          Example From in the inbox: {fromSampleLine(accountNameSample, connectedEmail)}.
          {' '}Display name comes from Account Admin (organization full name).
        </p>
      ) : (
        <p style={{ margin: '0 0 16px', fontSize: '13px', color: 'var(--app-text-muted)' }}>
          Connect a Google account with Gmail send permission. Only OAuth tokens are stored.
          On Google’s consent screen, check <strong>Send email on your behalf</strong> before Continue.
          {accountNameSample
            ? ` Example From once connected: ${fromSampleLine(accountNameSample, 'you@gmail.com')}.`
            : ''}
        </p>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {!connected ? (
          <button type="button" className="btn btn-primary" onClick={onConnect} disabled={busy || disabled}>
            {busy ? 'Redirecting to Google…' : connectLabel}
          </button>
        ) : (
          <button type="button" className="btn btn-secondary" onClick={onDisconnect} disabled={busy || disabled}>
            {busy ? 'Disconnecting…' : disconnectLabel}
          </button>
        )}
      </div>
    </>
  );
}

function EmailAuthCard({ title, children }) {
  return (
    <section
      style={{
        border: '1px solid var(--app-border)',
        borderRadius: '8px',
        padding: '20px',
        marginBottom: '24px',
        background: 'var(--app-canvas)',
        maxWidth: '560px',
      }}
    >
      <h2 style={{ fontSize: '18px', marginTop: 0, marginBottom: '12px' }}>{title}</h2>
      {children}
    </section>
  );
}

function pickInitialAuthMode(storedMode, oauthAppConfigured) {
  if (storedMode === 'oauth' || storedMode === 'smtp') return storedMode;
  return oauthAppConfigured ? 'oauth' : 'smtp';
}

function EmailServerTab({ account_id, isSuperAdmin, apiRole }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [bannerError, setBannerError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [status, setStatus] = useState(null);
  const [accountForm, setAccountForm] = useState(emptyEmailForm);
  const [systemForm, setSystemForm] = useState(emptyEmailForm);
  const [accountAuthMode, setAccountAuthMode] = useState('oauth');
  const [systemAuthMode, setSystemAuthMode] = useState('oauth');
  const [clearAccountDialog, setClearAccountDialog] = useState(false);
  const [clearSystemDialog, setClearSystemDialog] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const applyStatus = useCallback((data) => {
    setStatus(data);
    setAccountForm(formFromPublicSmtp(data?.email_smtp || {}));
    setSystemForm(formFromPublicSmtp(data?.system_email_smtp || {}));
    setAccountAuthMode(pickInitialAuthMode(data?.account_auth_mode, !!data?.oauthAppConfigured));
    setSystemAuthMode(pickInitialAuthMode(data?.system_auth_mode, !!data?.oauthAppConfigured));
  }, []);

  const loadStatus = useCallback(async () => {
    if (!account_id) return;
    setLoading(true);
    setBannerError('');
    try {
      const data = await getGmailOAuthStatus(account_id, apiRole);
      applyStatus(data);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setLoading(false);
    }
  }, [account_id, apiRole, applyStatus]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const gmail = searchParams.get('gmail');
    if (!gmail) return;
    if (gmail === 'connected') {
      setSuccessMsg('Gmail connected via OAuth.');
      loadStatus();
    } else if (
      gmail === 'error'
      || gmail === 'token_failed'
      || gmail === 'refresh_missing'
      || gmail === 'email_missing'
      || gmail === 'missing_code'
      || gmail === 'invalid_state'
    ) {
      setBannerError('Gmail connection failed. Try Connect with Google again.');
    }
    const next = new URLSearchParams(searchParams);
    next.delete('gmail');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, loadStatus]);

  function buildSmtpPayload(form, alreadyConfigured) {
    const payload = {
      host: form.host,
      port: form.port,
      secure: form.secure,
      user: form.user,
    };
    if (form.pass) payload.pass = form.pass;
    else if (!alreadyConfigured) payload.pass = '';
    return payload;
  }

  async function handleConnect(target) {
    setBusy(true);
    setBannerError('');
    setSuccessMsg('');
    try {
      const { url } = await getGmailOAuthAuthUrl({
        accountId: account_id,
        role: apiRole,
        target,
        returnUrl: '/Communication?tab=email',
      });
      if (url) window.location.href = url;
      else throw new Error('No authorization URL returned.');
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
      setBusy(false);
    }
  }

  async function handleDisconnectOauth(target) {
    setBusy(true);
    setBannerError('');
    setSuccessMsg('');
    try {
      await disconnectGmailOAuth({
        accountId: account_id,
        role: apiRole,
        target,
      });
      if (target === 'system') {
        setSuccessMsg('System default Gmail disconnected.');
        setClearSystemDialog(false);
      } else {
        setSuccessMsg('Account Gmail disconnected. Using system default when configured.');
        setClearAccountDialog(false);
      }
      await loadStatus();
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveAccountSmtp() {
    setBusy(true);
    setBannerError('');
    setSuccessMsg('');
    try {
      const smtpConfigured = status?.account_auth_mode === 'smtp';
      const data = await saveAccountEmailSmtp(
        account_id,
        buildSmtpPayload(accountForm, smtpConfigured)
      );
      applyStatus(data);
      setAccountForm((prev) => ({ ...prev, pass: '' }));
      setSuccessMsg('Account SMTP saved. OAuth for this account was cleared if previously connected.');
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleClearAccountSmtp() {
    setBusy(true);
    setBannerError('');
    setSuccessMsg('');
    try {
      const data = await clearAccountEmailSmtp(account_id);
      applyStatus(data);
      setSuccessMsg('Account SMTP cleared. Using system default when configured.');
      setClearAccountDialog(false);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveSystemSmtp() {
    setBusy(true);
    setBannerError('');
    setSuccessMsg('');
    try {
      const smtpConfigured = status?.system_auth_mode === 'smtp';
      const data = await saveSystemEmailSmtp(
        buildSmtpPayload(systemForm, smtpConfigured),
        account_id
      );
      applyStatus(data);
      setSystemForm((prev) => ({ ...prev, pass: '' }));
      setSuccessMsg('System default SMTP saved. System OAuth was cleared if previously connected.');
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleClearSystemSmtp() {
    setBusy(true);
    setBannerError('');
    setSuccessMsg('');
    try {
      const data = await clearSystemEmailSmtp(account_id);
      applyStatus(data);
      setSuccessMsg('System default SMTP cleared.');
      setClearSystemDialog(false);
    } catch (err) {
      setBannerError(getMessageForFetchError(err));
    } finally {
      setBusy(false);
    }
  }

  function handleClearConfirm() {
    if (isSuperAdmin) {
      if (systemAuthMode === 'smtp' && status?.system_auth_mode === 'smtp') {
        return handleClearSystemSmtp();
      }
      return handleDisconnectOauth('system');
    }
    if (accountAuthMode === 'smtp' && status?.account_auth_mode === 'smtp') {
      return handleClearAccountSmtp();
    }
    return handleDisconnectOauth('account');
  }

  if (loading) {
    return <p>Loading…</p>;
  }

  const oauthAppConfigured = !!status?.oauthAppConfigured;
  const systemOk = !!status?.systemDefaultConfigured;
  const usingOverride = !!status?.usingOverride;
  const systemEmail = (status?.system_email || '').trim();
  const accountEmail = (status?.account_email || '').trim();
  const accountNameSample = (status?.sample_account_name || '').trim();
  const smtpUserSample = (status?.sample_smtp_user || '').trim();
  const accountSmtpActive = status?.account_auth_mode === 'smtp';
  const systemSmtpActive = status?.system_auth_mode === 'smtp';

  return (
    <>
      <p style={{ color: 'var(--app-text-muted)', marginBottom: '12px' }}>
        {isSuperAdmin
          ? 'Authorize the platform default Gmail with Google OAuth or SMTP (app password). Choose one method.'
          : 'Authorize outbound Gmail with Google OAuth or SMTP (app password). Connect an account override, or use the system default.'}
      </p>
      <p style={{ fontSize: '13px', color: 'var(--app-text-muted)', marginBottom: '20px' }}>
        {isSuperAdmin
          ? 'Tenants without their own connection use this system default. If it is not configured, mail is not sent.'
          : 'Resolution order: account override → system default. If neither is configured, mail is not sent.'}
      </p>

      {bannerError && <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />}
      {successMsg && (
        <div style={{ padding: '12px', marginBottom: '16px', background: '#d1e7dd', color: '#0f5132', borderRadius: '6px' }}>
          {successMsg}
        </div>
      )}

      {!oauthAppConfigured && (isSuperAdmin ? systemAuthMode : accountAuthMode) === 'oauth' && (
        <div style={{ padding: '12px', marginBottom: '16px', background: '#fff3cd', color: '#664d03', borderRadius: '6px' }}>
          Gmail OAuth is not configured on the server. Set GOOGLE_GMAIL_CLIENT_ID and GOOGLE_GMAIL_CLIENT_SECRET
          (or reuse GOOGLE_CALENDAR_CLIENT_ID / SECRET) and add redirect URI …/gmailOAuth/callback in Google Cloud Console.
          Or choose SMTP (app password) instead.
        </div>
      )}

      {!isSuperAdmin && (
        <ConfirmationModal
          open={clearAccountDialog}
          title={accountSmtpActive ? 'Use system default' : 'Disconnect account Gmail'}
          message={
            accountSmtpActive
              ? "Clear this account's SMTP override? Outbound mail will use the system default again (when configured)."
              : "Disconnect this account's Gmail override? Outbound mail will use the system default again (when connected)."
          }
          mode="confirm"
          confirmLabel={accountSmtpActive ? 'Use system default' : 'Disconnect'}
          onCancel={() => setClearAccountDialog(false)}
          onConfirm={handleClearConfirm}
        />
      )}

      {isSuperAdmin && (
        <ConfirmationModal
          open={clearSystemDialog}
          title={systemSmtpActive ? 'Clear system default SMTP' : 'Disconnect system default'}
          message={
            systemSmtpActive
              ? 'Clear the platform default SMTP? Accounts without an override will not be able to send email until a default or override is configured.'
              : 'Disconnect the platform default Gmail? Accounts without an override will not be able to send email until a default or override is connected.'
          }
          mode="confirm"
          confirmLabel={systemSmtpActive ? 'Clear' : 'Disconnect'}
          onCancel={() => setClearSystemDialog(false)}
          onConfirm={handleClearConfirm}
        />
      )}

      {isSuperAdmin && (
        <EmailAuthCard title="System default Gmail">
          <AuthModeToggle
            idPrefix="system"
            value={systemAuthMode}
            onChange={setSystemAuthMode}
            disabled={busy}
          />
          {systemAuthMode === 'oauth' ? (
            <EmailOauthPanel
              statusLine={
                systemOk && status?.system_auth_mode === 'oauth' ? (
                  <span style={{ color: '#1a7f37', fontWeight: 500 }}>
                    Connected via OAuth{systemEmail ? `: ${systemEmail}` : ''}
                  </span>
                ) : (
                  <span style={{ color: 'var(--app-text-muted)' }}>
                    Not connected via OAuth — tenants without an override cannot send email
                    {systemOk && status?.system_auth_mode === 'smtp' ? ' (SMTP is currently active; switching to OAuth and connecting will replace it)' : ''}
                  </span>
                )
              }
              connected={systemOk && status?.system_auth_mode === 'oauth'}
              connectedEmail={systemEmail}
              accountNameSample={accountNameSample}
              onConnect={() => handleConnect('system')}
              onDisconnect={() => setClearSystemDialog(true)}
              connectLabel="Connect system Gmail"
              disconnectLabel="Disconnect system Gmail"
              busy={busy}
              disabled={!oauthAppConfigured}
            />
          ) : (
            <>
              <p style={{ margin: '0 0 12px', fontSize: '14px' }}>
                {systemOk && status?.system_auth_mode === 'smtp' ? (
                  <span style={{ color: '#1a7f37', fontWeight: 500 }}>
                    SMTP authorized{systemEmail ? `: ${systemEmail}` : ''}
                  </span>
                ) : (
                  <span style={{ color: 'var(--app-text-muted)' }}>
                    SMTP not configured — tenants without an override cannot send email
                  </span>
                )}
              </p>
              <SmtpFields
                idPrefix="system-smtp"
                form={systemForm}
                onField={(key, val) => setSystemForm((prev) => ({ ...prev, [key]: val }))}
                configured={systemSmtpActive}
                smtpUserPlaceholder={smtpUserSample}
                accountNameSample={accountNameSample}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveSystemSmtp}
                  disabled={busy}
                >
                  {busy ? 'Saving…' : systemSmtpActive ? 'Update system default' : 'Authorize system default'}
                </button>
                {systemSmtpActive && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setClearSystemDialog(true)}
                    disabled={busy}
                  >
                    Clear system default
                  </button>
                )}
              </div>
            </>
          )}
        </EmailAuthCard>
      )}

      {!isSuperAdmin && (
        <EmailAuthCard title="Account Gmail">
          <AuthModeToggle
            idPrefix="account"
            value={accountAuthMode}
            onChange={setAccountAuthMode}
            disabled={busy}
          />
          {accountAuthMode === 'oauth' ? (
            <EmailOauthPanel
              statusLine={
                usingOverride && status?.account_auth_mode === 'oauth' ? (
                  <span style={{ color: '#1a7f37', fontWeight: 500 }}>
                    Account override connected via OAuth{accountEmail ? `: ${accountEmail}` : ''}
                  </span>
                ) : (
                  <span style={{ color: 'var(--app-text-muted)' }}>
                    Using system default
                    {systemOk ? (systemEmail ? ` (${systemEmail})` : '') : ' (not configured yet)'}
                    {usingOverride && status?.account_auth_mode === 'smtp'
                      ? ' — SMTP override is active; connecting OAuth will replace it'
                      : ''}
                  </span>
                )
              }
              connected={usingOverride && status?.account_auth_mode === 'oauth'}
              connectedEmail={accountEmail}
              accountNameSample={accountNameSample}
              onConnect={() => handleConnect('account')}
              onDisconnect={() => setClearAccountDialog(true)}
              connectLabel="Connect account Gmail"
              disconnectLabel="Disconnect account Gmail"
              busy={busy}
              disabled={!oauthAppConfigured}
            />
          ) : (
            <>
              <p style={{ margin: '0 0 12px', fontSize: '14px' }}>
                {usingOverride && status?.account_auth_mode === 'smtp' ? (
                  <span style={{ color: '#1a7f37', fontWeight: 500 }}>
                    Account override via SMTP{accountEmail ? `: ${accountEmail}` : ''}
                  </span>
                ) : (
                  <span style={{ color: 'var(--app-text-muted)' }}>
                    Using system default
                    {systemOk ? (systemEmail ? ` (${systemEmail})` : '') : ' (not configured yet)'}
                  </span>
                )}
              </p>
              <SmtpFields
                idPrefix="account-smtp"
                form={accountForm}
                onField={(key, val) => setAccountForm((prev) => ({ ...prev, [key]: val }))}
                configured={accountSmtpActive}
                smtpUserPlaceholder={smtpUserSample}
                accountNameSample={accountNameSample}
              />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveAccountSmtp}
                  disabled={busy}
                >
                  {busy ? 'Saving…' : accountSmtpActive ? 'Update email server' : 'Authorize email server'}
                </button>
                {accountSmtpActive && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setClearAccountDialog(true)}
                    disabled={busy}
                  >
                    Use system default
                  </button>
                )}
              </div>
            </>
          )}
        </EmailAuthCard>
      )}
    </>
  );
}

function Communication() {
  const { userInfo, hasRole } = useAuth();
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const isSuperAdmin = hasRole('Super Admin');
  const apiRole = getApiRoleFromUserInfo(userInfo);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const activeTab =
    tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : DEFAULT_TAB;

  useEffect(() => {
    if (!tabFromUrl || !VALID_TABS.includes(tabFromUrl)) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [tabFromUrl, activeTab, setSearchParams]);

  if (!account_id) {
    return (
      <div className="page-container">
        <h1>Communication</h1>
        <p>You must be signed in with an account to manage communication settings.</p>
      </div>
    );
  }

  return (
    <div className="page-container page-container--section-tabs" style={{ maxWidth: '900px' }}>
      <div className="page-header">
        <h1>Communication</h1>
        <p className="page-subtitle">
          Message templates and outbound Gmail (OAuth or SMTP) for this organization.
        </p>
      </div>

      <div className="tabs-bar">
        <div className="tabs-list" role="tablist" aria-label="Communication sections">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_MESSAGES}
            className={`tab-btn ${activeTab === TAB_MESSAGES ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: TAB_MESSAGES })}
          >
            Message
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === TAB_EMAIL}
            className={`tab-btn ${activeTab === TAB_EMAIL ? 'active' : ''}`}
            onClick={() => setSearchParams({ tab: TAB_EMAIL })}
          >
            Email
          </button>
        </div>
      </div>

      <div className="tab-content" style={{ paddingTop: '16px' }}>
        {activeTab === TAB_MESSAGES && (
          <MessagesTab account_id={account_id} serverUrl={serverUrl} />
        )}
        {activeTab === TAB_EMAIL && (
          <EmailServerTab
            account_id={account_id}
            isSuperAdmin={isSuperAdmin}
            apiRole={apiRole}
          />
        )}
      </div>
    </div>
  );
}

export default Communication;
